import getNextConfig from '@maxpreps/shared/constants/getNextConfig';
import apiEnviroments from './apiEnvironments';
import { publicOrigin } from './origins';

export type TMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

export interface IFetchOptions {
  queryParams: any;
  headers: Record<string, string>;
  body: any;
  token: string;

  method: TMethod;
  /**
   * The datapoint that should be utilized on the JSON return object to get the real data (not the fluff wrapping stuff)
   */
  dataPoint: string;
  /**
   * Forces a request to WF
   */
  wfRequest?: boolean;
  /**
   * For debugging apis locally
   */
  isLocalTest?: boolean;
  /**
   * Forces WF requests to the WF secure servers
   */
  secureServer?: boolean;
  /**
   * "credentials: include" is used on some WF requests
   */
  credentials?: string;
}

const { appEnv } = getNextConfig().publicRuntimeConfig;

const formatPath = (url: string) => (url.charAt(0) !== '/' ? '/' : '') + url;

const getBaseOrigin = (options: IFetchOptions) => {
  if (options.isLocalTest) return 'https://localhost:44303';

  let baseOrigin = options.wfRequest
    ? options.secureServer
      ? publicOrigin.replace('https://', 'https://secure-')
      : publicOrigin
    : apiEnviroments[appEnv];

  // this is preferred route to know if code is executing on the server or client
  // https://flaviocopes.com/nextjs-server-client-code/
  // (MP-10800) use internal routing instead of the internet to get the data
  // LOCAL_PRODUCTION_BUILD_COMMENT_START
  if (typeof window === 'undefined' && appEnv !== 'dev') {
    options.headers = {
      ...options.headers,
      Host: `${baseOrigin.replace('https://', '')}`
    };

    baseOrigin = baseOrigin
      .replace('.com', '.internal')
      .replace('https://', 'http://');
  }
  // LOCAL_PRODUCTION_BUILD_COMMENT_END

  return baseOrigin;
};

export const getModifiedUrl = (url: string, options: IFetchOptions): URL => {
  const modifiedUrl = new URL(formatPath(url), getBaseOrigin(options));

  // helper to put an object or query params onto the fetch url
  if (options?.queryParams) {
    Object.keys(options.queryParams).forEach(key => {
      if (options.queryParams[key]) {
        modifiedUrl.searchParams.append(key, options.queryParams[key]);
      }
    });
  }

  // stuff to do only on the client
  if (typeof window !== 'undefined') {
    // stuff to do for webforms only
    if (options.wfRequest) {
      // This requestOrigin variable is needed on the client side to navigate CORS issues
      modifiedUrl.searchParams.append('origin', window.location.origin);
    }
  }

  return modifiedUrl;
};

export const tryAppendRequestContentTypeHeader = options => {
  // FormData sets the Content-Type header appropriately.
  if (typeof window === 'undefined' || options.body instanceof FormData) {
    return;
  }

  // Set content type for POSTs, PUTs, PATCHes, DELETES, as long as it wasn't specified and isn't FormData (Photo Uploads)
  if (
    (options.method?.toLowerCase() ?? 'get') === 'get' ||
    options.headers?.['Content-Type']
  ) {
    return;
  }

  // passed checks, add it
  options.headers = {
    ...options.headers,
    'Content-Type': 'application/json'
  };
};
